import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Headphones, Loader2, Volume2, Pause, ArrowRight, ArrowLeft,
  CheckCircle2, XCircle, RotateCcw, Trophy,
} from "lucide-react";

const SECTIONS = [
  { key: "comprehension_ecrite", title: "Compréhension écrite", icon: BookOpen, desc: "Lisez et répondez" },
  { key: "comprehension_orale", title: "Compréhension orale", icon: Headphones, desc: "Écoutez et répondez" },
];
const LEVELS = ["all", "A1", "A2", "B1", "B2", "C1", "C2"];
const LEVEL_LABEL = { all: "Tous" };

function speak(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "fr-FR";
  u.rate = 0.95;
  const frVoice = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith("fr"));
  if (frVoice) u.voice = frVoice;
  window.speechSynthesis.speak(u);
  return u;
}

export default function Practice() {
  const [params] = useSearchParams();
  const [step, setStep] = useState("select"); // select | quiz | results
  const [section, setSection] = useState(params.get("section") || "comprehension_ecrite");
  const [level, setLevel] = useState("all");
  const [loading, setLoading] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // question_id -> index
  const [result, setResult] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    // preload voices
    if ("speechSynthesis" in window) window.speechSynthesis.getVoices();
  }, []);

  const isOral = section === "comprehension_orale";

  const startQuiz = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/practice/${section}`, { params: { level, count: 10 } });
      if (!data.questions.length) {
        toast.error("Aucune question pour cette sélection.");
        return;
      }
      setQuestions(data.questions);
      setAnswers({});
      setCurrent(0);
      setResult(null);
      setStep("quiz");
    } catch (e) {
      toast.error("Impossible de charger les questions.");
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: idx }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        section, level,
        answers: questions.map((q) => ({ question_id: q.id, selected_index: answers[q.id] ?? -1 })),
      };
      const { data } = await api.post("/practice/submit", payload);
      setResult(data);
      setStep("results");
      window.speechSynthesis?.cancel();
    } catch (e) {
      toast.error("Erreur lors de la soumission.");
    } finally {
      setLoading(false);
    }
  };

  const q = questions[current];
  const answeredCount = Object.keys(answers).length;

  const toggleSpeak = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = speak(q.transcript);
    setSpeaking(true);
    if (u) u.onend = () => setSpeaking(false);
  };

  // ---------------- SELECT ----------------
  if (step === "select") {
    return (
      <div data-testid="practice-select">
        <h1 className="font-display text-4xl font-semibold">S'entraîner</h1>
        <p className="text-muted-foreground mt-2">Choisissez une épreuve et un niveau, puis lancez une série de 10 questions.</p>

        <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Épreuve</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.key;
            return (
              <button
                key={s.key} onClick={() => setSection(s.key)} data-testid={`select-section-${s.key}`}
                className={`text-left rounded-2xl border p-6 transition-all ${active ? "border-primary bg-accent shadow-md" : "border-border bg-card hover:border-primary/40"}`}
              >
                <div className={`h-11 w-11 rounded-xl grid place-items-center ${active ? "emerald-surface" : "bg-secondary"}`}>
                  <Icon className={`h-5 w-5 ${active ? "text-white" : "text-primary"}`} />
                </div>
                <h3 className="mt-3 font-semibold">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </button>
            );
          })}
        </div>

        <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Niveau</h2>
        <div className="flex flex-wrap gap-2">
          {LEVELS.map((l) => (
            <button
              key={l} onClick={() => setLevel(l)} data-testid={`select-level-${l}`}
              className={`px-4 h-10 rounded-full text-sm font-semibold transition-colors ${level === l ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-accent"}`}
            >
              {LEVEL_LABEL[l] || l}
            </button>
          ))}
        </div>

        <button
          onClick={startQuiz} disabled={loading} data-testid="start-quiz-btn"
          className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Lancer l'entraînement <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    );
  }

  // ---------------- RESULTS ----------------
  if (step === "results" && result) {
    return (
      <div data-testid="practice-results">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl emerald-surface grain relative overflow-hidden p-8 text-center">
          <Trophy className="h-10 w-10 mx-auto text-white/90" />
          <div className="mt-3 text-6xl font-display font-semibold text-white" data-testid="results-score">{result.score}%</div>
          <p className="mt-2 text-white/85">{result.correct} bonnes réponses sur {result.total}</p>
        </motion.div>

        <div className="mt-8 space-y-4">
          {result.results.map((r, i) => (
            <div key={r.question_id} className="rounded-2xl bg-card border border-border p-5" data-testid={`result-item-${i}`}>
              <div className="flex items-start gap-3">
                {r.is_correct ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />}
                <div className="flex-1">
                  {(r.context || r.transcript) && (
                    <p className="text-sm text-muted-foreground italic mb-2">{r.context || r.transcript}</p>
                  )}
                  <p className="font-medium">{r.question}</p>
                  <div className="mt-3 space-y-1.5">
                    {r.options.map((opt, oi) => {
                      const isCorrect = oi === r.correct_index;
                      const isChosen = oi === r.selected_index;
                      return (
                        <div key={oi} className={`text-sm px-3 py-2 rounded-lg border ${
                          isCorrect ? "border-primary bg-accent text-accent-foreground" :
                          isChosen ? "border-destructive bg-destructive/10" : "border-transparent"
                        }`}>
                          {opt}
                          {isCorrect && <span className="ml-2 text-xs font-semibold text-primary">✓ bonne réponse</span>}
                          {isChosen && !isCorrect && <span className="ml-2 text-xs font-semibold text-destructive">votre choix</span>}
                        </div>
                      );
                    })}
                  </div>
                  {r.explanation && <p className="mt-3 text-sm text-muted-foreground"><span className="font-semibold text-foreground">Explication : </span>{r.explanation}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={() => setStep("select")} data-testid="retry-btn" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
          <RotateCcw className="h-4 w-4" /> Nouvel entraînement
        </button>
      </div>
    );
  }

  // ---------------- QUIZ ----------------
  return (
    <div data-testid="practice-quiz">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-muted-foreground">Question {current + 1} / {questions.length}</span>
        <span className="text-xs px-2.5 py-1 rounded-full bg-secondary font-semibold">{q.level}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="mt-6 rounded-2xl bg-card border border-border p-6"
        >
          {isOral ? (
            <button onClick={toggleSpeak} data-testid="play-audio-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full emerald-surface text-white font-semibold mb-5">
              {speaking ? <Pause className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              {speaking ? "Pause" : "Écouter l'audio"}
            </button>
          ) : (
            q.context && <p className="text-muted-foreground italic mb-5 leading-relaxed border-l-2 border-primary/40 pl-4">{q.context}</p>
          )}

          <h2 className="text-xl font-semibold mb-5">{q.question}</h2>

          <div className="space-y-3">
            {q.options.map((opt, oi) => {
              const selected = answers[q.id] === oi;
              return (
                <button
                  key={oi} onClick={() => selectAnswer(q.id, oi)} data-testid={`option-${oi}`}
                  className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all flex items-center gap-3 ${
                    selected ? "border-primary bg-accent" : "border-border bg-background hover:border-primary/40"
                  }`}
                >
                  <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold shrink-0 ${selected ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {String.fromCharCode(65 + oi)}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}
          data-testid="prev-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border font-semibold disabled:opacity-40 hover:bg-secondary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Précédent
        </button>

        {current < questions.length - 1 ? (
          <button onClick={() => setCurrent((c) => c + 1)} data-testid="next-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity">
            Suivant <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={loading || answeredCount === 0} data-testid="submit-quiz-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Terminer ({answeredCount}/{questions.length})</>}
          </button>
        )}
      </div>
    </div>
  );
}
